import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { altioraToast } from "@/components/altiora/toast";
import { PageShell } from "@/components/altiora/PremiumUI";
import { AIThinkingState } from "@/components/AltioraComponents";
import {
  BookOpen, Sparkles, Target, Lightbulb, ListChecks, BookMarked, Users2,
  Building2, Calendar, TrendingUp, Gauge, ArrowRight, ExternalLink, Video,
  FileText, Headphones, GraduationCap, Landmark, Users, Wrench, X,
  CheckCircle2, Circle, Clock,
} from "lucide-react";

const KIND_ICON = {
  internal_video: Video, internal_pdf: FileText, guide: BookOpen, template: Wrench,
  checklist: ListChecks, training: GraduationCap, webinar: Video, faq: BookMarked,
  case_study: BookMarked, partner_page: Users2, gov_site: Landmark,
  community_org: Users, university: GraduationCap, youtube_video: Video,
  article: FileText, scientific_paper: FileText, book: BookOpen,
  podcast: Headphones, event: Calendar, conference: Calendar,
  expert_profile: Users2, mentor_profile: Users2,
};

const TRUST_CHIP = {
  verified: { label: "Vérifié", cls: "text-[#065F46] bg-[#D1FAE5]" },
  partner: { label: "Partenaire", cls: "text-[#0B2545] bg-[#E0E7FF]" },
  community: { label: "Communautaire", cls: "text-[#5B21B6] bg-[#EDE9FE]" },
  external: { label: "Externe sélectionné", cls: "text-[#B45309] bg-[#FEF3C7]" },
};

export default function Knowledge() {
  useI18n();
  const [journey, setJourney] = useState(null);       // active journey
  const [pastJourneys, setPastJourneys] = useState([]);
  const [meta, setMeta] = useState(null);              // resource-types
  const [profile, setProfile] = useState(null);        // learning profile
  const [showProfile, setShowProfile] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [busy, setBusy] = useState(false);
  const [goal, setGoal] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [m, p, list] = await Promise.all([
          api.get("/knowledge/resource-types"),
          api.get("/knowledge/profile"),
          api.get("/knowledge/journeys?limit=6"),
        ]);
        setMeta(m.data);
        setProfile(p.data);
        setPastJourneys(list.data.items || []);
        if ((list.data.items || []).length > 0) {
          const full = await api.get(`/knowledge/journeys/${list.data.items[0].id}`);
          setJourney(full.data);
        }
      } catch { altioraToast.error("Impossible de charger le Knowledge Orchestrator"); }
    })();
  }, []);

  const buildJourney = async () => {
    if (goal.trim().length < 8) { altioraToast.error("Décrivez votre objectif en 1-2 phrases (min 8 caractères)"); return; }
    setBusy(true);
    try {
      const r = await api.post("/knowledge/journey/build", { goal_fr: goal.trim() });
      setJourney(r.data);
      setGoal("");
      const list = await api.get("/knowledge/journeys?limit=6");
      setPastJourneys(list.data.items || []);
      altioraToast.success(`Parcours généré par ${r.data.llm_used || "IA"}`);
    } catch { altioraToast.error("Échec de la génération. L'orchestrateur IA a répondu par un fallback."); }
    finally { setBusy(false); }
  };

  const toggleProgress = async (idx) => {
    if (!journey) return;
    const current = journey.progress?.[idx]?.completed;
    try {
      const r = await api.post(`/knowledge/journeys/${journey.id}/progress`, {
        indicator_index: idx, completed: !current,
      });
      setJourney({ ...journey, progress: r.data.progress, completion_pct: r.data.completion_pct });
    } catch { altioraToast.error("Échec"); }
  };

  const openJourney = async (jid) => {
    try {
      const r = await api.get(`/knowledge/journeys/${jid}`);
      setJourney(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { altioraToast.error("Échec"); }
  };

  return (
    <PageShell testid="knowledge-root">
      {/* TopBar rendered globally */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-[#C5A880]" strokeWidth={1.5} />
              <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#5B5B62]">Knowledge Orchestrator™</div>
            </div>
            <h1 className="text-3xl font-display font-medium text-[#1F2937] tracking-tight">
              Chef d&apos;orchestre de vos parcours
            </h1>
            <p className="text-sm text-[#5B5B62] mt-1 max-w-2xl">
              ALTIORA ne renvoie jamais une liste de liens. Chaque objectif devient un parcours complet
              en 10 sections : diagnostic, actions, ressources, pros, échéancier, ROI.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowLibrary(true)}
              data-testid="knowledge-library-open"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm border border-[#E4E1D8] bg-white text-[#1F2937] hover:bg-[#F1EFE9] transition"
            >
              <BookMarked className="h-4 w-4" strokeWidth={1.5} /> Bibliothèque
            </button>
            <button
              onClick={() => setShowProfile(true)}
              data-testid="knowledge-profile-open"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm border border-[#E4E1D8] bg-white text-[#1F2937] hover:bg-[#F1EFE9] transition"
            >
              <Gauge className="h-4 w-4" strokeWidth={1.5} /> Mon profil d&apos;apprentissage
            </button>
          </div>
        </div>

        {/* Goal input */}
        <div className="bg-white border border-[#E4E1D8] rounded-lg p-5 mb-6" data-testid="knowledge-goal-box">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-2">
            Quel est votre objectif ?
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) buildJourney(); }}
              placeholder="Ex : Obtenir un emploi en cybersécurité au Québec en moins de 12 mois"
              className="flex-1 min-w-[280px] px-3 py-2 text-sm bg-white border border-[#E4E1D8] rounded-md focus:outline-none focus:border-[#0B2545]"
              data-testid="knowledge-goal-input"
            />
            <button
              onClick={buildJourney}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#0B2545] text-white rounded-md hover:bg-[#123163] disabled:opacity-50"
              data-testid="knowledge-build-journey"
            >
              {busy ? <><Sparkles className="h-4 w-4 animate-pulse" /> Orchestration…</> : <><Target className="h-4 w-4" /> Générer le parcours</>}
            </button>
          </div>
          <div className="text-[10px] text-[#8B8B8F] mt-2">
            L&apos;orchestrateur IA (Sonnet 4.6 → GPT-5.2 → Haiku 4.5) construit un parcours en 10 sections.
          </div>
        </div>

        {/* Active journey */}
        {busy ? (
          <AIThinkingState label="Orchestration du parcours en cours…" />
        ) : journey ? (
          <JourneyView journey={journey} onToggle={toggleProgress} />
        ) : (
          <div className="text-center py-16 text-[#5B5B62] bg-white border border-[#E4E1D8] rounded-lg">
            Aucun parcours généré pour le moment. Formulez votre objectif ci-dessus.
          </div>
        )}

        {/* Past journeys */}
        {pastJourneys.length > 1 && (
          <div className="mt-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-3">Parcours précédents</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {pastJourneys.slice(1).map((j) => (
                <button
                  key={j.id}
                  onClick={() => openJourney(j.id)}
                  className="text-left p-3 bg-white border border-[#E4E1D8] rounded-md hover:border-[#0B2545] transition"
                  data-testid="knowledge-past-journey"
                >
                  <div className="text-sm text-[#1F2937] line-clamp-2">{j.goal_fr}</div>
                  <div className="text-[10px] text-[#8B8B8F] mt-1">
                    {new Date(j.created_at).toLocaleDateString("fr-CA")} · {j.completion_pct || 0}% complété
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {showProfile && <ProfileModal profile={profile} meta={meta} onClose={() => setShowProfile(false)} onSaved={(p) => { setProfile(p); setShowProfile(false); }} />}
        {showLibrary && <LibraryModal meta={meta} onClose={() => setShowLibrary(false)} />}
      </div>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Journey view — 10 sections
// ---------------------------------------------------------------------------
function JourneyView({ journey, onToggle }) {
  const s = journey.sections || {};
  const resourcesById = Object.fromEntries((journey.hydrated_resources || []).map((r) => [r.id, r]));
  const prosById = Object.fromEntries((journey.hydrated_professionals || []).map((p) => [p.id, p]));

  return (
    <div className="space-y-5" data-testid="knowledge-journey-view">
      {/* Header card */}
      <div className="bg-white border border-[#E4E1D8] rounded-xl p-6 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-56 h-56 bg-[#C5A880]/10 rounded-full" />
        <div className="relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-2">Votre parcours</div>
          <h2 className="text-2xl font-display font-medium text-[#1F2937] tracking-tight leading-snug">
            {journey.goal_fr}
          </h2>
          <div className="flex items-center gap-2 mt-2 text-[11px] text-[#8B8B8F]">
            <Sparkles className="h-3.5 w-3.5" /> Généré par {journey.llm_used || "IA"}
            {journey.completion_pct != null && <span>· {journey.completion_pct}% complété</span>}
          </div>
        </div>
      </div>

      {/* 1 · Diagnostic + 2 · Raisonnement */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Section icon={Target} title="Diagnostic personnalisé" number="1">
          <p className="text-sm text-[#1F2937] leading-relaxed">{s.diagnostic_fr}</p>
        </Section>
        <Section icon={Lightbulb} title="Raisonnement stratégique" number="2">
          <p className="text-sm text-[#1F2937] leading-relaxed">{s.reasoning_fr}</p>
        </Section>
      </div>

      {/* 3 · Priority actions */}
      <Section icon={ListChecks} title="Actions prioritaires" number="3">
        <ol className="space-y-2">
          {(s.priority_actions || []).map((a, i) => (
            <li key={i} className="flex items-start gap-3 py-2 border-b border-[#F1EFE9] last:border-0">
              <div className="w-7 h-7 rounded-full bg-[#0B2545] text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                {a.order || i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1F2937]">{a.title_fr}</div>
                <div className="text-xs text-[#5B5B62] mt-0.5">{a.why_fr}</div>
              </div>
              {a.eta_hours != null && (
                <div className="text-[11px] text-[#8B8B8F] inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> ~{a.eta_hours}h
                </div>
              )}
            </li>
          ))}
        </ol>
      </Section>

      {/* 4 · Ranked resources */}
      <Section icon={BookMarked} title="Ressources classées pour vous" number="4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(s.ranked_resources || []).map((rr, i) => {
            const r = resourcesById[rr.resource_id];
            if (!r) return null;
            const Icon = KIND_ICON[r.kind] || BookMarked;
            const trust = TRUST_CHIP[r.trust_level] || TRUST_CHIP.community;
            return (
              <div key={rr.resource_id} className="p-3 rounded-md border border-[#E4E1D8] hover:border-[#C5A880] transition" data-testid="journey-resource">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-[#F1EFE9] flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium text-[#1F2937] leading-snug">{r.title}</div>
                      <div className="text-[10px] font-mono text-[#8B8B8F] flex-shrink-0">#{rr.order || i + 1}</div>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${trust.cls}`}>{trust.label}</span>
                      {r.duration_minutes && <span className="text-[10px] text-[#8B8B8F]">{r.duration_minutes} min</span>}
                      {r.cost === "free" && <span className="text-[10px] text-[#059669]">Gratuit</span>}
                      {r.lang && <span className="text-[10px] text-[#8B8B8F] uppercase">{r.lang}</span>}
                    </div>
                    <div className="mt-2 bg-[#FBF6EA] rounded px-2 py-1.5 text-[11px] text-[#7A5B14] italic border-l-2 border-[#C5A880]">
                      « {rr.why_fr} »
                    </div>
                    {r.url && (
                      <a href={r.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#0B2545] hover:text-[#123163]">
                        <ExternalLink className="h-3 w-3" /> Ouvrir
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* 5 · Professionals + 6 · Organizations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Section icon={Users2} title="Professionnels ALTIORA" number="5">
          {(s.professionals || []).length > 0 ? (
            <ul className="space-y-2">
              {s.professionals.map((p) => {
                const full = prosById[p.pro_id];
                return (
                  <li key={p.pro_id} className="p-2.5 rounded border border-[#E4E1D8]">
                    <div className="text-sm text-[#1F2937] font-medium">{full?.display_name || p.pro_id}</div>
                    <div className="text-xs text-[#5B5B62]">{p.role}</div>
                    <div className="text-[11px] text-[#7A5B14] italic mt-1">« {p.why_fr} »</div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-[#8B8B8F]">Aucun professionnel spécifiquement recommandé — l&apos;objectif est faisable en autonomie.</p>
          )}
        </Section>
        <Section icon={Building2} title="Organismes utiles" number="6">
          <ul className="space-y-2">
            {(s.organizations || []).map((o, i) => (
              <li key={i} className="p-2.5 rounded border border-[#E4E1D8]">
                <div className="text-sm text-[#1F2937] font-medium">{o.name}</div>
                {o.kind && <div className="text-[10px] font-mono uppercase tracking-wide text-[#8B8B8F]">{o.kind}</div>}
                <div className="text-[11px] text-[#5B5B62] mt-1">{o.why_fr}</div>
              </li>
            ))}
          </ul>
        </Section>
      </div>

      {/* 7 · Timeline */}
      <Section icon={Calendar} title="Échéancier" number="7">
        <div className="relative pl-6 space-y-3">
          <div className="absolute left-2 top-2 bottom-2 w-px bg-[#E4E1D8]" />
          {(s.timeline || []).map((t, i) => (
            <div key={i} className="relative">
              <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-[#0B2545] text-white text-[10px] flex items-center justify-center">{t.step || i + 1}</div>
              <div className="text-sm text-[#1F2937]">{t.label_fr}</div>
              <div className="text-[11px] text-[#8B8B8F]">Échéance : J+{t.deadline_days} jours</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 8 · ROI + 9 · Progress + 10 · Next step */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Section icon={TrendingUp} title="Retour sur investissement" number="8">
          <div className="text-sm text-[#059669] font-medium">{s.roi_estimate_fr}</div>
        </Section>
        <Section icon={Gauge} title="Indicateurs de progression" number="9">
          <ul className="space-y-1.5">
            {(s.progress_indicators || []).map((ind, i) => {
              const done = journey.progress?.[i]?.completed;
              return (
                <li key={i}>
                  <button onClick={() => onToggle(i)} className="w-full flex items-start gap-2 text-left p-1.5 rounded hover:bg-[#F1EFE9]" data-testid="journey-indicator">
                    {done ? <CheckCircle2 className="h-4 w-4 text-[#059669] flex-shrink-0 mt-0.5" /> : <Circle className="h-4 w-4 text-[#8B8B8F] flex-shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs ${done ? "line-through text-[#8B8B8F]" : "text-[#1F2937]"}`}>{ind.metric_fr}</div>
                      <div className="text-[10px] text-[#8B8B8F]">Objectif : {ind.target}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </Section>
        <Section icon={ArrowRight} title="Prochaine étape (48h)" number="10">
          <div className="bg-[#0B2545] text-white rounded-md p-3 text-sm leading-relaxed">
            {s.next_step_fr}
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, number, children }) {
  return (
    <div className="bg-white border border-[#E4E1D8] rounded-lg p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded bg-[#F1EFE9] flex items-center justify-center text-[10px] font-mono text-[#5B5B62]">{number}</div>
        <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
        <h3 className="text-sm font-display font-medium text-[#1F2937] tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile modal
// ---------------------------------------------------------------------------
function ProfileModal({ profile, meta, onClose, onSaved }) {
  const [form, setForm] = useState({
    knowledge_level: profile?.knowledge_level || "intermediate",
    languages: profile?.languages || ["fr"],
    profession: profile?.profession || "",
    age_range: profile?.age_range || "",
    goals: profile?.goals || [],
    availability_hours_per_week: profile?.availability_hours_per_week ?? 5,
    budget_monthly: profile?.budget_monthly ?? 0,
    learning_styles: profile?.learning_styles || ["video", "reading"],
  });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggleArr = (k, v) => set(k, form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v]);
  const inputCls = "w-full px-2.5 py-1.5 text-sm bg-white border border-[#E4E1D8] rounded-md focus:outline-none focus:border-[#0B2545]";
  const save = async () => {
    try {
      const r = await api.post("/knowledge/profile", form);
      altioraToast.success("Profil d'apprentissage enregistré");
      onSaved(r.data.profile);
    } catch { altioraToast.error("Échec"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="knowledge-profile-modal">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-display text-[#1F2937]">Profil d&apos;apprentissage</h3>
          <button onClick={onClose} className="p-1 text-[#5B5B62]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <Field label="Niveau de connaissances">
            <select className={inputCls} value={form.knowledge_level} onChange={(e) => set("knowledge_level", e.target.value)}>
              <option value="beginner">Débutant</option>
              <option value="intermediate">Intermédiaire</option>
              <option value="advanced">Avancé</option>
              <option value="expert">Expert</option>
            </select>
          </Field>
          <Field label="Langues"><div className="flex gap-2 flex-wrap">
            {["fr", "en", "ar", "es"].map((l) => (
              <button key={l} onClick={() => toggleArr("languages", l)} className={`px-2.5 py-1 text-xs rounded-full border ${form.languages.includes(l) ? "bg-[#0B2545] text-white border-[#0B2545]" : "bg-white text-[#5B5B62] border-[#E4E1D8]"}`}>{l.toUpperCase()}</button>
            ))}
          </div></Field>
          <Field label="Profession actuelle"><input className={inputCls} value={form.profession} onChange={(e) => set("profession", e.target.value)} placeholder="Ex: Comptable, Développeur…" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Disponibilité (h/semaine)"><input type="number" min="0" max="80" className={inputCls} value={form.availability_hours_per_week} onChange={(e) => set("availability_hours_per_week", Number(e.target.value))} /></Field>
            <Field label="Budget mensuel ($)"><input type="number" min="0" className={inputCls} value={form.budget_monthly} onChange={(e) => set("budget_monthly", Number(e.target.value))} /></Field>
          </div>
          <Field label="Styles d'apprentissage">
            <div className="flex gap-2 flex-wrap">
              {(meta?.learning_styles || ["reading", "video", "audio", "hands_on"]).map((s) => (
                <button key={s} onClick={() => toggleArr("learning_styles", s)} className={`px-2.5 py-1 text-xs rounded-full border ${form.learning_styles.includes(s) ? "bg-[#0B2545] text-white border-[#0B2545]" : "bg-white text-[#5B5B62] border-[#E4E1D8]"}`}>
                  {s === "reading" ? "Lecture" : s === "video" ? "Vidéo" : s === "audio" ? "Audio" : "Pratique"}
                </button>
              ))}
            </div>
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-[#E4E1D8] flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#5B5B62]">Annuler</button>
          <button onClick={save} className="px-4 py-1.5 text-sm bg-[#0B2545] text-white rounded-md hover:bg-[#123163]" data-testid="knowledge-profile-save">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-1">{label}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library modal (browse resources)
// ---------------------------------------------------------------------------
function LibraryModal({ meta, onClose }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (kind) params.set("kind", kind);
      const r = await api.get(`/knowledge/resources?${params.toString()}&limit=60`);
      setRows(r.data.items || []);
    } catch { altioraToast.error("Échec"); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="knowledge-library-modal">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-display text-[#1F2937]">Bibliothèque ALTIORA — {rows.length} ressources</h3>
          <button onClick={onClose} className="p-1 text-[#5B5B62]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-4 flex gap-2 flex-wrap border-b border-[#E4E1D8]">
          <input placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 min-w-[200px] px-2.5 py-1.5 text-sm border border-[#E4E1D8] rounded-md" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="px-2.5 py-1.5 text-sm border border-[#E4E1D8] rounded-md">
            <option value="">Tous types</option>
            {(meta?.kinds || []).map((k) => <option key={k.key} value={k.key}>{k.label_fr}</option>)}
          </select>
          <button onClick={load} className="px-3 py-1.5 text-sm bg-[#0B2545] text-white rounded-md">Filtrer</button>
        </div>
        <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((r) => {
            const Icon = KIND_ICON[r.kind] || BookMarked;
            const trust = TRUST_CHIP[r.trust_level] || TRUST_CHIP.community;
            return (
              <div key={r.id} className="p-3 rounded-md border border-[#E4E1D8]">
                <div className="flex items-start gap-2">
                  <Icon className="h-4 w-4 text-[#0B2545] mt-0.5" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1F2937]">{r.title}</div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${trust.cls}`}>{trust.label}</span>
                      <span className="text-[10px] text-[#8B8B8F] uppercase">{r.lang}</span>
                      {r.cost === "free" && <span className="text-[10px] text-[#059669]">Gratuit</span>}
                    </div>
                    <div className="text-[11px] text-[#5B5B62] mt-1 line-clamp-2">{r.summary_fr}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
